import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
import MetaTags from '../../utils/seo/MetaTags';
import StructuredData from '../../utils/seo/StructuredData';

/**
 * SEOHead Component
 * Manages all SEO meta tags and structured data for the current page
 *
 * Usage:
 * <SEOHead
 *   title="Page Title"
 *   description="Page description"
 *   image="/path/to/image.jpg"
 *   product={productData} // For product pages
 *   breadcrumbs={[{name: 'Home', url: '/'}, {name: 'Products'}]}
 * />
 */
const SEOHead = ({
  title,
  description,
  keywords = [],
  image,
  type = 'website',
  product,
  products,
  breadcrumbs = [],
  noIndex = false,
  canonical,
  children,
}) => {
  const location = useLocation();
  const { language } = useLanguage();

  useEffect(() => {
    // Clear previous dynamic meta and structured data
    MetaTags.clearDynamicMeta();
    StructuredData.clearAllStructuredData();

    // Setup base structured data (organization, website)
    StructuredData.setupBaseStructuredData();

    // Determine current URL path
    const currentPath = location.pathname;
    const currentUrl = canonical || currentPath;

    // Handle product pages
    if (product) {
      MetaTags.setProductMeta(product, language);
      StructuredData.setProductStructuredData(product, breadcrumbs, language);
      return;
    }

    // Handle product list pages (shop, categories)
    if (products && products.length > 0) {
      const listMeta = {
        name: title || 'Products',
        url: currentPath,
      };
      StructuredData.setProductListStructuredData(products, listMeta, breadcrumbs);
    }

    // Handle regular pages
    MetaTags.setPageMeta({
      title,
      description,
      keywords,
      image,
      url: currentUrl,
      type,
      noIndex,
    }, language);

    // Add breadcrumb structured data if provided
    if (breadcrumbs.length > 0) {
      const breadcrumbSchema = StructuredData.getBreadcrumbSchema(breadcrumbs);
      if (breadcrumbSchema) {
        StructuredData.setStructuredData(breadcrumbSchema, 'breadcrumb-schema');
      }
    }

  }, [title, description, keywords, image, type, product, products, breadcrumbs, noIndex, canonical, location.pathname, language]);

  // This component doesn't render anything
  return children || null;
};

/**
 * Specialized SEO components for common page types
 */

/**
 * HomePage SEO
 */
export const HomePageSEO = ({ language = 'fa' }) => {
  const metaData = {
    fa: {
      title: 'لاویسا - اینجا راحت خرید کن!',
      description: 'فروشگاه آنلاین لاویسا با بهترین برندهای مد و پوشاک ترکیه و جهان. خرید آسان، ارسال سریع، تضمین کیفیت و قیمت مناسب.',
      keywords: ['فروشگاه آنلاین', 'خرید لباس', 'مد', 'پوشاک', 'برندهای ترکیه', 'لاویسا'],
    },
    en: {
      title: 'Lavvissa - Shop with Ease!',
      description: 'Lavvissa online fashion store featuring top Turkish and international brands. Easy shopping, fast delivery, quality and price guaranteed.',
      keywords: ['online store', 'fashion', 'clothing', 'Turkish brands', 'Lavvissa'],
    },
  };

  const data = metaData[language] || metaData.fa;

  return (
    <SEOHead
      title={data.title}
      description={data.description}
      keywords={data.keywords}
      type="website"
      image="/landing.png"
    />
  );
};

/**
 * ProductPage SEO
 */
export const ProductPageSEO = ({ product, breadcrumbs = [], language = 'fa' }) => {
  if (!product) return null;

  return (
    <SEOHead
      product={product}
      breadcrumbs={breadcrumbs}
    />
  );
};

/**
 * ShopPage SEO
 */
export const ShopPageSEO = ({ products = [], category, breadcrumbs = [], language = 'fa' }) => {
  const metaData = {
    fa: {
      title: category ? `${category.name?.fa} - فروشگاه لاویسا` : 'فروشگاه - لاویسا',
      description: category
        ? `خرید ${category.name?.fa} از بهترین برندهای ترکیه و جهان در فروشگاه آنلاین لاویسا`
        : 'محصولات مد و پوشاک در فروشگاه آنلاین لاویسا. جدیدترین کلکسیون‌های برندهای معتبر جهان',
    },
    en: {
      title: category ? `${category.name?.en} - Lavvissa Shop` : 'Shop - Lavvissa',
      description: category
        ? `Shop ${category.name?.en} from top Turkish and international brands at Lavvissa`
        : 'Fashion and clothing products at Lavvissa online store. Latest collections from world-renowned brands',
    },
  };

  const data = metaData[language] || metaData.fa;

  return (
    <SEOHead
      title={data.title}
      description={data.description}
      products={products}
      breadcrumbs={breadcrumbs}
      type="website"
    />
  );
};

/**
 * BlogPost SEO
 */
export const BlogPostSEO = ({ post, breadcrumbs = [], language = 'fa' }) => {
  useEffect(() => {
    if (!post) return;
    // Article structured data
    const articleSchema = {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title?.[language] || post.title,
      description: post.excerpt?.[language] || post.excerpt,
      image: post.image,
      datePublished: post.publishedAt,
      dateModified: post.updatedAt,
      author: {
        '@type': 'Person',
        name: post.author?.name || 'Lavvissa',
      },
      publisher: {
        '@type': 'Organization',
        name: 'Lavvissa',
        logo: {
          '@type': 'ImageObject',
          url: `${MetaTags.DEFAULT_METADATA.siteUrl}/lavvissa-logo.png`,
        },
      },
    };

    StructuredData.setStructuredData(articleSchema, 'article-schema');
  }, [post, language]);

  if (!post) return null;

  return (
    <SEOHead
      title={post.title?.[language] || post.title}
      description={post.excerpt?.[language] || post.excerpt}
      image={post.image}
      type="article"
      breadcrumbs={breadcrumbs}
    />
  );
};

/**
 * FAQ Page SEO
 */
export const FAQPageSEO = ({ faqs = [], language = 'fa' }) => {
  useEffect(() => {
    const faqSchema = StructuredData.getFAQSchema(faqs);
    if (faqSchema) {
      StructuredData.setStructuredData(faqSchema, 'faq-schema');
    }
  }, [faqs]);

  const metaData = {
    fa: {
      title: 'سوالات متداول - لاویسا',
      description: 'پاسخ به سوالات متداول درباره خرید، ارسال، مرجوعی و سایر خدمات فروشگاه آنلاین لاویسا',
    },
    en: {
      title: 'FAQ - Lavvissa',
      description: 'Frequently asked questions about shopping, shipping, returns and other Lavvissa services',
    },
  };

  const data = metaData[language] || metaData.fa;

  return (
    <SEOHead
      title={data.title}
      description={data.description}
      type="website"
    />
  );
};

/**
 * Contact Page SEO
 */
export const ContactPageSEO = ({ language = 'fa' }) => {
  useEffect(() => {
    const contactSchema = {
      '@context': 'https://schema.org',
      '@type': 'ContactPage',
      name: language === 'fa' ? 'تماس با ما' : 'Contact Us',
      description: language === 'fa'
        ? 'راه‌های ارتباط با پشتیبانی فروشگاه آنلاین لاویسا'
        : 'Ways to contact Lavvissa online store support',
    };

    StructuredData.setStructuredData(contactSchema, 'contact-schema');
  }, [language]);

  const metaData = {
    fa: {
      title: 'تماس با ما - لاویسا',
      description: 'با پشتیبانی لاویسا در ارتباط باشید. پاسخگویی 24 ساعته به سوالات شما',
    },
    en: {
      title: 'Contact Us - Lavvissa',
      description: 'Get in touch with Lavvissa support. 24/7 response to your questions',
    },
  };

  const data = metaData[language] || metaData.fa;

  return (
    <SEOHead
      title={data.title}
      description={data.description}
      type="website"
    />
  );
};

/**
 * 404 Page SEO
 */
export const NotFoundPageSEO = ({ language = 'fa' }) => {
  const metaData = {
    fa: {
      title: 'صفحه یافت نشد - 404',
      description: 'متاسفانه صفحه مورد نظر شما یافت نشد',
    },
    en: {
      title: 'Page Not Found - 404',
      description: 'Sorry, the page you are looking for could not be found',
    },
  };

  const data = metaData[language] || metaData.fa;

  return (
    <SEOHead
      title={data.title}
      description={data.description}
      noIndex={true}
    />
  );
};

export default SEOHead;
